import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trash2, UserX, UserCheck, Shield, Download, Users, BarChart3, FileAudio, TrendingUp, Eraser, XCircle, Target } from 'lucide-react';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../auth';

interface UserStat {
  id: string;
  username: string;
  is_active: number;
  is_admin: number;
  onboarded: boolean;
  annotation_count: number;
  assigned_remaining: number;
  tier: Tier | null;
  quota: number | null;
  quota_remaining: number | null;
  quota_note: string | null;
  total_seconds: number;
  mean_seconds: number;
  median_seconds: number;
  percent_done: number;
  not_listened_to_the_end: number;
  responses: Record<string, unknown> | null;
  last_login: string | null;
  created_at: string;
}

type Tier = 'reliable' | 'calibration' | 'blocked';

const TIER_BADGE: Record<Tier, string> = {
  reliable: 'bg-green-500',
  calibration: 'bg-amber-500',
  blocked: 'bg-red-500',
};

interface ReliableCoverage {
  zero: number;
  one: number;
  atTarget: number;
  ratingsNeeded: number;
  target: number;
}

interface Stats {
  users: UserStat[];
  totalUsers: number;
  totalAnnotations: number;
  totalSamples: number;
  onboardedUsers: number;
  meanAnnotationTime: number;
  medianAnnotationTime: number;
  annotatedOnce: number;
  annotatedTwice: number;
  annotatedThreeTimes: number;
  annotatedMoreThanThree: number;
  reliableCoverage: ReliableCoverage;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [quotaEdit, setQuotaEdit] = useState<{ tier: Tier; quota: string; note: string }>({ tier: 'reliable', quota: '0', note: '' });
  const { token } = useAuth();

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      toast.success(`User ${username} deleted successfully`);
      fetchStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteAnnotations = async (userId: string, username: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/annotations`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete annotations');
      }

      const data = await response.json();
      toast.success(`${username}: ${data.message}`);
      fetchStats();
    } catch (error) {
      console.error('Error deleting annotations:', error);
      toast.error('Failed to delete annotations');
    }
  };

  const handleUnassignSamples = async (userId: string, username: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/assignments`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to unassign samples');
      }

      const data = await response.json();
      toast.success(`${username}: ${data.message}`);
      fetchStats();
    } catch (error) {
      console.error('Error unassigning samples:', error);
      toast.error('Failed to unassign samples');
    }
  };

  const handleSaveQuota = async (userId: string, username: string) => {
    const quota = Number(quotaEdit.quota);
    if (!Number.isInteger(quota) || quota < 0) {
      toast.error('Quota must be a non-negative whole number');
      return;
    }
    try {
      const response = await fetch(`/api/admin/users/${userId}/quota`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier: quotaEdit.tier, quota, note: quotaEdit.note }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update quota');
      }

      toast.success(`${username}: ${quotaEdit.tier}, ${quota} more annotations`);
      fetchStats();
    } catch (error) {
      console.error('Error updating quota:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update quota');
    }
  };

  const handleToggleUserStatus = async (userId: string, username: string, currentStatus: boolean) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      toast.success(`User ${username} ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchStats();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await fetch('/api/admin/annotations/csv', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'annotations.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const handleDownloadUsersCSV = async () => {
    try {
      const response = await fetch('/api/admin/users/csv', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Users CSV downloaded');
    } catch {
      toast.error('Failed to download users CSV');
    }
  };

  const handleDownloadFlagsCSV = async () => {
    try {
      const response = await fetch('/api/admin/flags/csv', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'flags.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Flags CSV downloaded');
    } catch {
      toast.error('Failed to download flags CSV');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (isActive: number) => {
    return isActive === 1 ? (
      <Badge variant="default" className="bg-green-500">Active</Badge>
    ) : (
      <Badge variant="destructive">Inactive</Badge>
    );
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6" />
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadUsersCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Download Users CSV
          </Button>
          <Button onClick={handleDownloadCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Download Annotations CSV
          </Button>
          <Button onClick={handleDownloadFlagsCSV} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Download Flags CSV
          </Button>
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Users</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalUsers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <UserCheck className="h-4 w-4" />
              <span className="text-sm">Onboarded</span>
            </div>
            <div className="text-3xl font-bold">{stats.onboardedUsers}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <BarChart3 className="h-4 w-4" />
              <span className="text-sm">Total Annotations</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalAnnotations}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileAudio className="h-4 w-4" />
              <span className="text-sm">Audio Samples</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalSamples}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Mean Time/Sample</span>
            </div>
            <div className="text-3xl font-bold">{stats.meanAnnotationTime}s</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Median Time/Sample</span>
            </div>
            <div className="text-3xl font-bold">{stats.medianAnnotationTime}s</div>
          </CardContent>
        </Card>
      </div>

      {/* Reliable Coverage (annotation plan) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Reliable Coverage (target: {stats.reliableCoverage.target} per sample)
          </CardTitle>
          <CardDescription>
            Only ratings from annotators in the "reliable" tier count. Users without a quota yet are new annotators and count as reliable.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="text-sm font-medium text-muted-foreground mb-1">0 reliable ratings</div>
                <div className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.reliableCoverage.zero}</div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="text-sm font-medium text-muted-foreground mb-1">1 reliable rating</div>
                <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{stats.reliableCoverage.one}</div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="text-sm font-medium text-muted-foreground mb-1">At target</div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.reliableCoverage.atTarget}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.totalSamples > 0 ? Math.round((stats.reliableCoverage.atTarget / stats.totalSamples) * 100) : 0}% of total
                </div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="text-sm font-medium text-muted-foreground mb-1">Reliable ratings still needed</div>
                <div className="text-3xl font-bold">{stats.reliableCoverage.ratingsNeeded}</div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Annotation Coverage Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Annotation Coverage</CardTitle>
          <CardDescription>
            Distribution of samples by number of annotations received
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-sm font-medium">Annotated Once</span>
                </div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.annotatedOnce}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.totalSamples > 0 ? Math.round((stats.annotatedOnce / stats.totalSamples) * 100) : 0}% of total
                </div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-sm font-medium">Annotated Twice</span>
                </div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.annotatedTwice}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.totalSamples > 0 ? Math.round((stats.annotatedTwice / stats.totalSamples) * 100) : 0}% of total
                </div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-sm font-medium">Annotated 3 Times</span>
                </div>
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.annotatedThreeTimes}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.totalSamples > 0 ? Math.round((stats.annotatedThreeTimes / stats.totalSamples) * 100) : 0}% of total
                </div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <span className="text-sm font-medium">Annotated 3+ Times</span>
                </div>
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.annotatedMoreThanThree}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.totalSamples > 0 ? Math.round((stats.annotatedMoreThanThree / stats.totalSamples) * 100) : 0}% of total
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>
            Manage all registered users, view onboarding status and annotation progress.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border [&_[data-slot=table-container]]:overflow-visible">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Onboarded</TableHead>
                  <TableHead>Annotations</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Tier / Quota left</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div className="relative group inline-block">
                          <span className="cursor-default">
                            {user.username}
                            {user.is_admin === 1 && (
                              <Badge variant="secondary" className="ml-2 text-xs">Admin</Badge>
                            )}
                          </span>
                          {user.responses && (
                            <div className="absolute left-0 bottom-full mb-1 z-50 hidden group-hover:block bg-popover border rounded-md shadow-lg p-3 text-sm min-w-[250px] max-w-[400px]">
                              <div className="font-semibold mb-2">Onboarding Responses</div>
                              {Object.entries(user.responses).map(([key, value]) => (
                                <div key={key} className="flex justify-between gap-4 py-0.5">
                                  <span className="text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
                                  <span className="font-medium text-right">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(user.is_active)}</TableCell>
                      <TableCell>
                        {user.onboarded ? (
                          <Badge variant="default" className="bg-green-500">Yes</Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="relative group/stats inline-block">
                          <span className="cursor-default">{user.annotation_count}</span>
                          {user.annotation_count > 0 && (
                            <div className="absolute left-0 bottom-full mb-1 z-50 hidden group-hover/stats:block bg-popover border rounded-md shadow-lg p-3 text-sm min-w-[200px]">
                              <div className="font-semibold mb-2">Annotation Stats</div>
                              <div className="flex justify-between gap-4 py-0.5">
                                <span className="text-muted-foreground">Total time:</span>
                                <span className="font-medium">{user.total_seconds}s</span>
                              </div>
                              <div className="flex justify-between gap-4 py-0.5">
                                <span className="text-muted-foreground">Mean time:</span>
                                <span className="font-medium">{user.mean_seconds}s</span>
                              </div>
                              <div className="flex justify-between gap-4 py-0.5">
                                <span className="text-muted-foreground">Median time:</span>
                                <span className="font-medium">{user.median_seconds}s</span>
                              </div>
                              <div className="flex justify-between gap-4 py-0.5">
                                <span className="text-muted-foreground">Progress:</span>
                                <span className="font-medium">{user.percent_done}%</span>
                              </div>
                              <div className="flex justify-between gap-4 py-0.5">
                                <span className="text-muted-foreground">Not listened:</span>
                                <span className="font-medium">{user.not_listened_to_the_end}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.assigned_remaining > 0 ? (
                          <Badge variant="secondary">{user.assigned_remaining}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 cursor-pointer"
                              title={user.quota_note ?? 'No quota yet: gets the default for new annotators on first task'}
                              onClick={() => setQuotaEdit({
                                tier: user.tier ?? 'reliable',
                                quota: String(user.quota_remaining ?? user.quota ?? 0),
                                note: user.quota_note ?? '',
                              })}
                            >
                              {user.tier ? (
                                <>
                                  <Badge variant="default" className={TIER_BADGE[user.tier]}>{user.tier}</Badge>
                                  <span>{user.quota_remaining} / {user.quota}</span>
                                </>
                              ) : (
                                <Badge variant="outline">default</Badge>
                              )}
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Annotation quota: {user.username}</AlertDialogTitle>
                              <AlertDialogDescription>
                                Reliable: rates least-covered samples, counts towards coverage. Calibration: rates samples
                                reliable annotators already rated, does not count. Blocked: gets no tasks.
                                The quota is the number of additional annotations from now on.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="space-y-3">
                              <select
                                className="border-input h-9 w-full rounded-md border bg-transparent px-3 text-sm"
                                value={quotaEdit.tier}
                                onChange={(e) => setQuotaEdit({ ...quotaEdit, tier: e.target.value as Tier })}
                              >
                                <option value="reliable">reliable</option>
                                <option value="calibration">calibration</option>
                                <option value="blocked">blocked</option>
                              </select>
                              <Input
                                type="number"
                                min={0}
                                value={quotaEdit.quota}
                                onChange={(e) => setQuotaEdit({ ...quotaEdit, quota: e.target.value })}
                                placeholder="Additional annotations"
                              />
                              <Input
                                value={quotaEdit.note}
                                onChange={(e) => setQuotaEdit({ ...quotaEdit, note: e.target.value })}
                                placeholder="Note (optional)"
                              />
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleSaveQuota(user.id, user.username)}>
                                Save
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                      <TableCell>{formatDate(user.last_login)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUserStatus(user.id, user.username, user.is_active === 1)}
                          >
                            {user.is_active === 1 ? (
                              <>
                                <UserX className="h-4 w-4 mr-1" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="h-4 w-4 mr-1" />
                                Activate
                              </>
                            )}
                          </Button>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive border-destructive hover:bg-destructive/10">
                                <Eraser className="h-4 w-4 mr-1" />
                                Clear Annotations
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Clear Annotations</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete all {user.annotation_count} annotations
                                  from user "{user.username}"? This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteAnnotations(user.id, user.username)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete Annotations
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          {user.assigned_remaining > 0 && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-orange-600 border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950">
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Unassign
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Unassign Samples</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to unassign {user.assigned_remaining} samples
                                    from user "{user.username}"? These samples will become available for other annotators.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleUnassignSamples(user.id, user.username)}
                                    className="bg-orange-600 text-white hover:bg-orange-700"
                                  >
                                    Unassign Samples
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete user "{user.username}"? 
                                  This action cannot be undone and will permanently remove 
                                  all user data.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user.id, user.username)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete User
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}